import { useEffect, useRef, useState } from 'react'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'

function segmenter(): { segment(s: string): Iterable<{ segment: string }> } | undefined {
  const Segmenter = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: string }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter
  return Segmenter ? new Segmenter(undefined, { granularity: 'grapheme' }) : undefined
}

/** The first user-perceived character of `value` — an emoji counts as one. */
export function firstGrapheme(value: string): string {
  if (!value) return ''
  const s = segmenter()
  if (s) {
    for (const seg of s.segment(value)) return seg.segment
  }
  return [...value][0] ?? ''
}

/**
 * The last user-perceived character of `value` — an emoji counts as one.
 * Used when the field's new value is LONGER than the current symbol: the
 * field held one grapheme already, so a longer value is the existing one
 * plus whatever was just typed at the caret, and the just-typed character is
 * always the one the field's own change just added — first-grapheme would
 * keep the character that was already there and drop it, so the field never
 * advanced past the first keystroke.
 */
export function lastGrapheme(value: string): string {
  if (!value) return ''
  const s = segmenter()
  if (s) {
    let last = ''
    for (const seg of s.segment(value)) last = seg.segment
    return last
  }
  return [...value].at(-1) ?? ''
}

/**
 * The symbol a `custom` pin draws — one character or emoji, as on mobile
 * (`showPinSymbolPicker`). Opened when a user picks the `custom` type in the
 * palette or the edit card, and from the edit card's Symbol row.
 */
export function PinSymbolDialog({
  initial,
  onConfirm,
  onCancel,
}: {
  initial: string
  onConfirm: (symbol: string) => void
  onCancel: () => void
}) {
  const { t } = useTranslation('map')
  const [symbol, setSymbol] = useState(firstGrapheme(initial))
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select() }, [])

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onKeyDown={(e) => { if (e.key === 'Escape') onCancel() }}
    >
      <div style={{ background: COLORS.surface, borderRadius: 12, padding: 24, maxWidth: 320, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
        <label htmlFor="pin-symbol-input" style={{ display: 'block', margin: '0 0 8px', color: COLORS.onSurface, fontSize: 16, fontWeight: 600 }}>
          {t('pinSymbol.title')}
        </label>
        <input
          id="pin-symbol-input"
          ref={inputRef}
          value={symbol}
          onChange={(e) => {
            const next = e.target.value
            setSymbol(next.length > symbol.length ? lastGrapheme(next) : firstGrapheme(next))
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && symbol) onConfirm(symbol) }}
          style={{ width: 64, height: 64, fontSize: 32, textAlign: 'center', borderRadius: 8, border: `1px solid ${COLORS.outlineVariant}`, background: COLORS.surfaceContainerLowest, color: COLORS.onSurface, margin: '0 auto 8px', display: 'block' }}
        />
        <p style={{ margin: '0 0 20px', color: COLORS.onSurfaceVariant, fontSize: 13, textAlign: 'center' }}>{t('pinSymbol.hint')}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="wh-btn wh-btn-text" onClick={onCancel}>{t('actions.cancel', { ns: 'common' })}</button>
          <button type="button" className="wh-btn wh-btn-primary" disabled={!symbol} onClick={() => onConfirm(symbol)}>{t('actions.save', { ns: 'common' })}</button>
        </div>
      </div>
    </div>
  )
}
